import * as _ from "lodash";
import * as moment from "moment-timezone";
import * as React from "react";

import {
  getDeliveryCutoff,
  getDeliveryWeekLeadTimes
} from "src/core/drops/utils/inherited-settings";
import { useDrop } from "src/core/drops/hooks";
import useTimezone from "src/core/shared/hooks/time-helpers/useTimeZone";
import { IProductLeadTimeGroup } from "src/core/products/api/products.api";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";

/**
 * check if an order date for a drop is in the additions period
 * @param orderDate
 * @param product
 */
const useAllowedLeadTimeGroups = () => {
  const [ { drop } ] = useDrop();
  const timezone = useTimezone();
  const [ { globalSettings } ] = useGlobalSettings();

  const getLeadTimeGroupsAllowed = React.useCallback(
    (orderDate: Date): Array<IProductLeadTimeGroup["id"]> => {
      if (!drop) {
        return [];
      }

      const weekLeadTimes = getDeliveryWeekLeadTimes(drop.groupedSettings);
      const deliveryCutoff = getDeliveryCutoff(drop.groupedSettings);

      if (!weekLeadTimes || !deliveryCutoff) {
        return [];
      }

      const allowedLeadTimeGroups: string[] = [];
      const deliveryDate = moment.utc(orderDate);
      const dayOfWeek = deliveryDate.isoWeekday();

      for (const leadTimeGroupKey of _.keys(weekLeadTimes.deliveryWeekLeadTimes)) {
        const leadTimeDaySetting = weekLeadTimes.deliveryWeekLeadTimes[ leadTimeGroupKey ][ dayOfWeek ];
        const dayCutoffTime = leadTimeDaySetting.cutoffTime || deliveryCutoff.time;

        if (dayCutoffTime) {
          const orderByDate = moment
            .tz(deliveryDate, timezone)
            .day(leadTimeDaySetting.day)
            .hours(parseInt(dayCutoffTime.split(":")[ 0 ]))
            .minutes(parseInt(dayCutoffTime.split(":")[ 1 ]));

          if (moment.tz(deliveryDate, timezone).isoWeekday() < leadTimeDaySetting.day) {
            orderByDate.subtract(1, "week");
          }

          if (leadTimeDaySetting.week) {
            orderByDate.subtract(leadTimeDaySetting.week, "week");
          }

          if (moment.utc().isBefore(orderByDate)) {
            allowedLeadTimeGroups.push(leadTimeGroupKey);
          }
        }
      }

      return _.compact(allowedLeadTimeGroups.map(leadTimeGroupName => _.find(globalSettings?.leadTimeGroups, ltg => ltg.name === leadTimeGroupName)?.id));
    },
    [
      drop,
      globalSettings?.leadTimeGroups,
      timezone
    ]
  );

  return getLeadTimeGroupsAllowed;
};

export default useAllowedLeadTimeGroups;
